import { z } from "zod";
import { photoRouletteCandidateSchema } from "./candidate.js";
import { photoRouletteGameConfigSchema } from "./config.js";
import { photoRouletteCancelReasonSchema, photoRouletteReactionEmojiSchema } from "./game.js";
import { photoRoulettePlayerSchema } from "./player.js";
import {
	PHOTO_ROULETTE_MAX_OPTIONS_PER_ROUND,
	PHOTO_ROULETTE_MIN_OPTIONS_PER_ROUND,
	photoRoulettePerPlayerRoundResultSchema,
	photoRouletteRankingEntrySchema,
} from "./round.js";

export const PHOTO_ROULETTE_ENVELOPE_TYPES = {
	LOBBY_UPDATED: "pr_lobby_updated",
	GAME_STARTED: "pr_game_started",
	ROUND_STARTED: "pr_round_started",
	ANSWER_RECEIVED: "pr_answer_received",
	VOTE_REVEALED: "pr_vote_revealed",
	ROUND_ENDED: "pr_round_ended",
	GAME_ENDED: "pr_game_ended",
	GAME_CANCELED: "pr_game_canceled",
	REACTION_RECEIVED: "pr_reaction_received",
	TAP_RECEIVED: "pr_tap_received",
} as const;

export type PhotoRouletteEnvelopeType = (typeof PHOTO_ROULETTE_ENVELOPE_TYPES)[keyof typeof PHOTO_ROULETTE_ENVELOPE_TYPES];

export const photoRouletteLobbyUpdatedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.LOBBY_UPDATED),
	game_id: z.string(),
	host_user_id: z.string(),
	players: z.array(photoRoulettePlayerSchema),
	game_config: photoRouletteGameConfigSchema.optional(),
	auto_start_at: z.number().int().positive().nullable(),
});
export type PhotoRouletteLobbyUpdatedEnvelope = z.infer<typeof photoRouletteLobbyUpdatedSchema>;

export const photoRouletteGameStartedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.GAME_STARTED),
	game_id: z.string(),
	rounds_count: z.number().int().positive(),
	candidates: z.array(photoRouletteCandidateSchema),
});
export type PhotoRouletteGameStartedEnvelope = z.infer<typeof photoRouletteGameStartedSchema>;

export const photoRouletteRoundStartedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.ROUND_STARTED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	post_id: z.string(),
	photo_url: z.string(),
	started_at: z.number().int().positive(),
	ends_at: z.number().int().positive(),
	options: z.array(photoRouletteCandidateSchema).min(PHOTO_ROULETTE_MIN_OPTIONS_PER_ROUND).max(PHOTO_ROULETTE_MAX_OPTIONS_PER_ROUND),
});
export type PhotoRouletteRoundStartedEnvelope = z.infer<typeof photoRouletteRoundStartedSchema>;

export const photoRouletteAnswerReceivedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.ANSWER_RECEIVED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	user_id: z.string(),
	answered_at: z.number().int().positive(),
});
export type PhotoRouletteAnswerReceivedEnvelope = z.infer<typeof photoRouletteAnswerReceivedSchema>;

export const photoRouletteVoteRevealedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.VOTE_REVEALED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	user_id: z.string(),
	guessed_user_id: z.string(),
});
export type PhotoRouletteVoteRevealedEnvelope = z.infer<typeof photoRouletteVoteRevealedSchema>;

export const photoRouletteRoundEndedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.ROUND_ENDED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	correct_user_id: z.string(),
	scores: z.record(z.string(), z.number().int().nonnegative()),
	per_player_round: z.array(photoRoulettePerPlayerRoundResultSchema),
	session_leaderboard: z.array(photoRouletteRankingEntrySchema),
});
export type PhotoRouletteRoundEndedEnvelope = z.infer<typeof photoRouletteRoundEndedSchema>;

export const photoRouletteGameEndedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.GAME_ENDED),
	game_id: z.string(),
	scores: z.record(z.string(), z.number().int().nonnegative()),
	ranking: z.array(photoRouletteRankingEntrySchema),
});
export type PhotoRouletteGameEndedEnvelope = z.infer<typeof photoRouletteGameEndedSchema>;

export const photoRouletteGameCanceledSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.GAME_CANCELED),
	game_id: z.string(),
	reason: photoRouletteCancelReasonSchema,
});
export type PhotoRouletteGameCanceledEnvelope = z.infer<typeof photoRouletteGameCanceledSchema>;

export const photoRouletteReactionReceivedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.REACTION_RECEIVED),
	game_id: z.string(),
	reaction_id: z.string(),
	user_id: z.string(),
	display_name: z.string(),
	avatar_url: z.string().nullable(),
	emoji: photoRouletteReactionEmojiSchema,
	created_at: z.number().int().positive(),
});
export type PhotoRouletteReactionReceivedEnvelope = z.infer<typeof photoRouletteReactionReceivedSchema>;

export const photoRouletteTapReceivedSchema = z.object({
	type: z.literal(PHOTO_ROULETTE_ENVELOPE_TYPES.TAP_RECEIVED),
	game_id: z.string(),
	from_user_id: z.string(),
	from_display_name: z.string(),
	from_avatar_url: z.string().nullable(),
	target_user_id: z.string(),
	created_at: z.number().int().positive(),
});
export type PhotoRouletteTapReceivedEnvelope = z.infer<typeof photoRouletteTapReceivedSchema>;

export const photoRouletteEnvelopeSchemas = [
	photoRouletteLobbyUpdatedSchema,
	photoRouletteGameStartedSchema,
	photoRouletteRoundStartedSchema,
	photoRouletteAnswerReceivedSchema,
	photoRouletteVoteRevealedSchema,
	photoRouletteRoundEndedSchema,
	photoRouletteGameEndedSchema,
	photoRouletteGameCanceledSchema,
	photoRouletteReactionReceivedSchema,
	photoRouletteTapReceivedSchema,
] as const;

export const PHOTO_ROULETTE_ENVELOPE_TYPE_SET = new Set<string>(Object.values(PHOTO_ROULETTE_ENVELOPE_TYPES));

export type PhotoRouletteServerEnvelope =
	| PhotoRouletteLobbyUpdatedEnvelope
	| PhotoRouletteGameStartedEnvelope
	| PhotoRouletteRoundStartedEnvelope
	| PhotoRouletteAnswerReceivedEnvelope
	| PhotoRouletteVoteRevealedEnvelope
	| PhotoRouletteRoundEndedEnvelope
	| PhotoRouletteGameEndedEnvelope
	| PhotoRouletteGameCanceledEnvelope
	| PhotoRouletteReactionReceivedEnvelope
	| PhotoRouletteTapReceivedEnvelope;
